import { ActionIcon, Group, Stack, Text } from "@mantine/core";
import { useParams } from "@tanstack/react-router";
import { Archive, ArrowUp, Image as ImageIcon, Layers2 } from "lucide-react";
import { useEffect, useState } from "react";

import type { Catalog, Job } from "../../../api/generated/model";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { usePricedStart } from "../../../platform/jobs/priced-start";
import { UploadState } from "../../../platform/media/image-upload";
import type { PickedImage } from "../../../platform/media/ImagePicker";
import { ProductPicker } from "../../../platform/media/ProductPicker";
import { ReferencesDialog } from "../../../platform/media/ReferencesDialog";
import { Choice } from "../../../ui/Choice";
import { GlassPanel } from "../../../ui/GlassPanel";
import { ModelPicker } from "../../../ui/ModelPicker";
import { Price } from "../../../ui/Price";
import { SourceTool } from "../../../ui/SourceTool";
import { AdCopiesModal } from "../ad-copies/AdCopiesModal";
import { STYLE_TILE } from "../ad-copies/picks";
import type { Tile } from "../ad-copies/SavedCopies";
import classes from "../composer/composer.module.css";
import { RatioGlyph } from "../../../ui/RatioGlyph";
import type { DraftShape } from "../draft-state";
import { ADVANCED_REFERENCES, COUNT_TIP, UNPRICED, helperText } from "./advanced";
import { AdvancedChips, AdvancedSwitch } from "./AdvancedMode";
import image from "./image.module.css";
import { SourceThumb } from "./SourceThumb";
import { StyleActions, StyleText } from "./StyleEditor";
import { ANALYSIS_REFUSED, useAdStructures } from "../ad-copies/use-ad-structures";
import { selectUpTo } from "../../../ui/format";
import { useAdvanced, type AdvancedCarry } from "./use-advanced";
import { UseStyleOrImage } from "./UseStyleOrImage";
import { useReferenceDrop } from "./useReferenceDrop";

const ROUTE = "/signed-in/teams/$teamId/brands/$brandId";

export interface AdvancedComposerProps {
  /** In a card of its own, not floating (`GlassPanel`'s `inline`, the Pro Studio redesign). */
  inline?: boolean | undefined;
  catalog: Catalog;
  /** What the simple composer held when the switch went on. */
  carry: AdvancedCarry;
  /** The switch going off, with what the simple composer keeps. */
  onSwitchOff: (back: AdvancedCarry) => void;
  onShapeChange: (shape: DraftShape) => void;
  onStarted: (job: Job) => void;
}

/**
 * Live's image Advanced mode (#234, captures studio-depth 24 to 28 and advanced-234): the switch
 * on, with the Ad copies and Style chips; live's line in place of the prompt and the references
 * under it, each analysed; at the footer's left Products (one) and Reference images (five), at its
 * right the models that serve `image.advanced`, the ratio, the count of images the ticked ad
 * copies make, the price and the start. The Ad copies modal ticks the copies; "Use Style or
 * Image?" asks before references replace the Style's.
 */
export function AdvancedComposer(props: AdvancedComposerProps) {
  const { catalog, carry, onSwitchOff, onShapeChange, onStarted } = props;
  const { teamId, brandId } = useParams({ from: ROUTE });
  const a = useAdvanced(catalog, carry);
  const structures = useAdStructures(
    teamId,
    brandId,
    a.references.map((each) => each.assetId),
  );
  const [collapsed, setCollapsed] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [referencesOpen, setReferencesOpen] = useState(false);
  const [copiesOpen, setCopiesOpen] = useState(false);
  // The Style editor's draft while it is open, else null.
  const [styleText, setStyleText] = useState<string | null>(null);
  // References waiting on "Use Style or Image?": taking them, if the answer is "Use Image".
  const [asking, setAsking] = useState<(() => void) | null>(null);
  const ask = (take: () => void) => {
    if (!a.mustAsk) take();
    else setAsking(() => take);
  };
  const full = a.references.length >= ADVANCED_REFERENCES;
  const drop = useReferenceDrop({
    addReference: a.addReference,
    room: ADVANCED_REFERENCES - a.references.length,
    canTake: true,
    ask: a.mustAsk ? ask : undefined,
  });
  const analysing = structures.analysing;
  const ready = !analysing && a.request !== undefined;
  const job = usePricedStart({
    teamId,
    brandId,
    quote: ready ? a.request : undefined,
    request: ready ? a.request : undefined,
    onStarted,
  });
  const { aspectRatio, total } = a;
  // The page's empty My Generations shows a card per image the ticks make.
  useEffect(() => {
    onShapeChange({ outputs: total, aspectRatio });
  }, [aspectRatio, total, onShapeChange]);
  const tiles: Tile[] =
    a.references.length === 0
      ? [{ id: STYLE_TILE, url: null, name: a.style, structure: null }]
      : a.references.map((each) => {
          const analysis = structures.of(each.assetId);
          return {
            id: each.assetId,
            url: each.url,
            name: each.name,
            structure:
              analysis.state === "ready" ? (analysis.structure?.text_elements ?? null) : null,
          };
        });
  const header = (
    <Group gap={20} wrap="nowrap">
      <AdvancedSwitch on onChange={() => onSwitchOff(a.carry())} />
      <AdvancedChips
        canSetup={a.canSetup}
        analysing={analysing}
        total={total}
        onOpenCopies={() => setCopiesOpen(true)}
        onClearCopies={() => a.setPicks({})}
        style={a.style}
        styleOpen={styleText !== null}
        onToggleStyle={() => setStyleText((open) => (open === null ? a.style : null))}
        onResetStyle={() => a.setStyle("")}
      />
    </Group>
  );
  const tools = (
    <Group gap={12} wrap="nowrap">
      <ProductPicker
        teamId={teamId}
        brandId={brandId}
        subtitle={selectUpTo(1, "product")}
        chosen={a.product}
        opened={productsOpen}
        onClose={() => setProductsOpen(false)}
        onConfirm={a.setProduct}
        anchor={(wrap) => (
          <SourceTool
            label="Products"
            icon={<Archive size={20} strokeWidth={1.5} />}
            view={a.product}
            removeLabel="Remove the product"
            onRemove={() => a.setProduct(null)}
            onClick={() => setProductsOpen((open) => !open)}
            open={productsOpen}
            target={wrap}
          />
        )}
      />
      <SourceTool
        label="Reference images"
        icon={<ImageIcon size={20} strokeWidth={1.5} />}
        view={null}
        removeLabel="Remove the reference"
        onRemove={() => undefined}
        onClick={() => setReferencesOpen(true)}
        disabled={full}
      />
    </Group>
  );
  const controls = (
    <>
      <ModelPicker models={a.models} value={a.model.id} onChange={a.chooseModel} />
      <Choice
        setting="Aspect ratio"
        icon={<RatioGlyph ratio={a.aspectRatio} />}
        width={100}
        value={a.aspectRatio}
        options={a.model.aspect_ratios.values.map((each) => ({ value: each, label: each }))}
        onChange={a.setAspectRatio}
      />
      <Choice
        setting="Outputs"
        icon={<Layers2 size={20} strokeWidth={1.5} />}
        width={80}
        value={String(total)}
        options={[{ value: String(total), label: String(total) }]}
        onChange={() => undefined}
        disabledBecause={COUNT_TIP}
      />
    </>
  );
  const start = (
    <>
      <Price priced={job.priced} pending={job.pricing} unpriced={UNPRICED} />
      <ActionIcon
        variant="filled"
        size="lg"
        radius="100%"
        aria-label="Start"
        disabled={!job.canStart}
        loading={job.start.isPending}
        onClick={job.startJob}
      >
        <ArrowUp size={20} />
      </ActionIcon>
    </>
  );
  const summary = (
    <span className={image.advancedSummary}>
      <span className={image.advancedBadge}>Advanced</span>
      <span className={image.advancedCount}>
        <Layers2 size={16} aria-hidden />
        {total}
      </span>
    </span>
  );
  const takeReferences = (next: PickedImage[]) => {
    const added = next.filter((each) => !a.references.some((had) => had.assetId === each.assetId));
    if (added.length === 0) a.setReferences(next);
    else ask(() => a.setReferences(next));
  };
  return (
    <GlassPanel
      inline={props.inline}
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      drop={drop}
      header={header}
      summary={<span className={classes.summary}>{summary}</span>}
      above={undefined}
      footer={
        styleText !== null ? (
          <StyleActions
            text={styleText}
            onCancel={() => setStyleText(null)}
            onSave={() => {
              a.setStyle(styleText);
              setStyleText(null);
            }}
          />
        ) : props.inline === true ? (
          // In a card, the shape of Pro's other composers: the settings wrap in their own space,
          // and the price and the start keep the row's end together.
          <Group justify="space-between" wrap="nowrap" gap={12}>
            <Group gap={12} wrap="nowrap">
              {tools}
              {controls}
            </Group>
            <Group gap={12} wrap="nowrap">
              {start}
            </Group>
          </Group>
        ) : (
          <Group justify="space-between" wrap="nowrap" gap={12}>
            {tools}
            <Group gap={12} wrap="nowrap">
              {controls}
              {start}
            </Group>
          </Group>
        )
      }
    >
      {styleText === null ? (
        <Stack gap={4}>
          <Text size="sm" c="var(--cai-color-text-muted)">
            {helperText(a.canSetup, analysing)}
          </Text>
          {a.references.length === 0 ? null : (
            <div className={image.thumbs}>
              {a.references.map((each) => (
                <SourceThumb
                  key={each.assetId}
                  view={each}
                  removeLabel={`Remove ${each.name}`}
                  onRemove={() => a.removeReference(each.assetId)}
                  analysing={structures.of(each.assetId).state === "analysing"}
                />
              ))}
            </div>
          )}
          <UploadState upload={drop.upload} />
          {drop.problem === null ? null : (
            <Text size="sm" c="var(--cai-color-text-danger)" role="alert">
              {drop.problem}
            </Text>
          )}
          {structures.refused ? (
            <Text size="sm" c="var(--cai-color-text-danger)" role="alert">
              {ANALYSIS_REFUSED}
            </Text>
          ) : null}
          {job.quoteError === null ? null : <ErrorAlert error={job.quoteError} />}
          {job.start.isError ? <ErrorAlert error={job.start.error} /> : null}
        </Stack>
      ) : (
        <StyleText text={styleText} onChange={setStyleText} />
      )}
      {referencesOpen ? (
        <ReferencesDialog
          teamId={teamId}
          brandId={brandId}
          title="Image References"
          subtitle={selectUpTo(ADVANCED_REFERENCES, "reference")}
          max={ADVANCED_REFERENCES}
          chosen={a.references.map((each) => each.assetId)}
          onClose={() => setReferencesOpen(false)}
          onConfirm={takeReferences}
        />
      ) : null}
      {a.product === null ? null : (
        <AdCopiesModal
          opened={copiesOpen}
          onClose={() => setCopiesOpen(false)}
          teamId={teamId}
          brandId={brandId}
          product={{ productId: a.product.productId, name: a.product.name, url: a.product.url }}
          inspiration={a.references[0] ?? null}
          tiles={tiles}
          confirmed={a.picks}
          onConfirm={(picks) => {
            a.setPicks(picks);
            setCopiesOpen(false);
          }}
          onClear={() => {
            a.setPicks({});
            setCopiesOpen(false);
          }}
        />
      )}
      <UseStyleOrImage
        opened={asking !== null}
        onKeepStyle={() => setAsking(null)}
        onUseImage={() => {
          a.clearForImages();
          asking?.();
          setAsking(null);
        }}
      />
    </GlassPanel>
  );
}
